import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { matchPath } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Catálogo de Módulos (26 entradas) — fonte ÚNICA do menu e da proteção de
 * rotas do ERP. A disponibilidade é calculada no backend
 * (GET /api/catalog/me): status global da plataforma, contrato do tenant,
 * perfil, ajustes do usuário e reserva de plataforma. O frontend nunca
 * decide acesso; ele apenas esconde o que o backend já nega.
 */
const CatalogContext = createContext(null);

export function CatalogProvider({ children }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setEntries([]);
      setLoaded(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/catalog/me");
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Mudanças de permissão (outra aba, administração ou o próprio usuário)
  // e retorno à aba revalidam o catálogo.
  useEffect(() => {
    const onChange = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("astral:authorization-changed", onChange);
    window.addEventListener("astral:catalog-changed", onChange);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("astral:authorization-changed", onChange);
      window.removeEventListener("astral:catalog-changed", onChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const value = useMemo(() => {
    const byKey = Object.fromEntries(entries.map((entry) => [entry.key, entry]));
    const isAvailable = (key) => Boolean(byKey[key]?.available);
    const entryForPath = (pathname) =>
      entries.find((entry) =>
        (entry.routes || [entry.route]).some((pattern) =>
          matchPath({ path: pattern, end: true }, pathname),
        ),
      ) || null;
    const byCategory = (category) =>
      entries
        .filter((entry) => entry.category === category && entry.available)
        .sort((a, b) => a.order - b.order);
    return { entries, byKey, isAvailable, entryForPath, byCategory, loading, loaded, error, refresh };
  }, [entries, loading, loaded, error, refresh]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error("useCatalog deve ser usado dentro de <CatalogProvider>.");
  }
  return context;
}

/** Notifica o app de que o catálogo (ou acesso) mudou. */
export function notifyCatalogChanged() {
  window.dispatchEvent(new Event("astral:catalog-changed"));
}
